/**
 * Every value on the page, as a table.
 *
 * This is the accessible twin of the charts above, and it is not a lesser view: the
 * timeline strip, the meters and the sparklines all encode with colour and position,
 * and this is where the same numbers are readable without either. It is also the fastest
 * way to compare fifteen checks across five hosts, which a grid of cards is not.
 *
 * A real `<table>` with scoped headers, so a screen reader announces each cell with the
 * check and the target it belongs to.
 */

import { Fragment } from 'react'

import type { HistorySummary, TargetResult } from '../api/types'
import { cn } from '../lib/cn'
import { EM_DASH, formatMs, formatPct, formatRelative } from '../lib/format'
import { SERVICE_PRESENTATION, STATUS_PRESENTATION, statusText } from '../lib/status'

interface TableViewProps {
  results: TargetResult[]
  history: Record<string, HistorySummary>
}

export function TableView({ results, history }: TableViewProps) {
  return (
    <div className="card overflow-x-auto">
      <table className="w-full min-w-[62rem] border-collapse text-xs">
        <caption className="px-4 py-3 text-left text-xs text-ink-secondary">
          Every target and check, with the values the charts above encode as colour and
          position.
        </caption>
        <thead>
          <tr className="border-y text-left text-ink-muted">
            <th scope="col" className="px-4 py-2 font-medium">
              Target
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Status
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              HTTP
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Round trip
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Answered
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Healthy
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              p95
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              View
            </th>
            <th scope="col" className="px-4 py-2 font-medium">
              Checked
            </th>
          </tr>
        </thead>

        <tbody>
          {results.map((result) => {
            const entry = history[result.target_id]
            const presentation = STATUS_PRESENTATION[result.status]
            const Icon = presentation.icon

            return (
              <Fragment key={result.target_id}>
                <tr className="border-b bg-surface-sunken/40">
                  <th scope="row" className="px-4 py-2.5 text-left font-semibold text-ink">
                    {result.site_name}{' '}
                    <span className="font-normal text-ink-muted">
                      · {result.env_label} · {result.host}
                    </span>
                  </th>
                  <td className={cn('px-3 py-2.5', statusText(presentation.token))}>
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <Icon size={13} aria-hidden />
                      {presentation.label}
                    </span>
                  </td>
                  <td className="tabular px-3 py-2.5 text-ink">
                    {result.http_status ?? EM_DASH}
                  </td>
                  <td className="tabular px-3 py-2.5 text-ink">{formatMs(result.elapsed_ms)}</td>
                  <td className="tabular px-3 py-2.5 text-ink">
                    {formatPct(entry?.available_pct ?? null)}
                  </td>
                  <td className="tabular px-3 py-2.5 text-ink">
                    {formatPct(entry?.healthy_pct ?? null)}
                  </td>
                  <td className="tabular px-3 py-2.5 text-ink">
                    {formatMs(entry?.latency_p95_ms ?? null)}
                  </td>
                  <td className="px-3 py-2.5 text-ink-secondary">
                    {result.view ?? EM_DASH}
                  </td>
                  <td className="px-4 py-2.5 text-ink-secondary">
                    {formatRelative(result.checked_at)}
                  </td>
                </tr>

                {result.services.map((service) => {
                  const servicePresentation = SERVICE_PRESENTATION[service.status]
                  const ServiceIcon = servicePresentation.icon
                  return (
                    <tr key={`${result.target_id}-${service.name}`} className="border-b">
                      <th
                        scope="row"
                        className="px-4 py-2 pl-8 text-left font-normal text-ink-secondary"
                      >
                        {service.name}
                        {service.important === false ? (
                          <span className="ml-1.5 text-ink-muted">(non-critical)</span>
                        ) : null}
                      </th>
                      <td className={cn('px-3 py-2', statusText(servicePresentation.token))}>
                        <span className="inline-flex items-center gap-1.5">
                          <ServiceIcon size={12} aria-hidden />
                          {servicePresentation.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-ink-muted">{EM_DASH}</td>
                      <td className="tabular px-3 py-2 text-ink">
                        {formatMs(service.latency_ms)}
                      </td>
                      <td className="px-3 py-2 text-ink-muted" colSpan={4}>
                        {service.detail ?? 'Detail needs an API key'}
                      </td>
                      <td className="px-4 py-2 text-ink-muted">
                        {service.important === null ? 'importance unknown' : ''}
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
